import { memo, RefObject } from 'react'
import { useSelector } from 'react-redux'
import { DEFAULT_EAGER_IMAGES, LOADING_TYPE } from '~/app/constants'
import { getQuantitySelector, getViewId } from '~/app/store/config'
import { GalleryTileEntityProvider } from '~/app/features/Gallery/components/PreviewArea/GalleryTileEntityProvider'
import { AnalyticsNames, ANALYTICS_EVENT_ACTIONS, PREVIEW_TYPE, ANALYTICS_LABEL } from 'shared/constants'
import { useAnalytics } from '~/app/features/Gallery/hooks/useAnalytics'
import { designByIdSelector } from '~/app/store/design'
import { getEntityImpressionId } from '~/app/store/analytics/entityImpressionId'
import { generateImpressionId } from '~/app/store/analytics/reducer'
import { DesignTile } from '~/app/features/Gallery/components/DesignTile'
import { designPersonalizationContextIdSelector } from '~/app/store/designPersonalization/selectors'
import { useFavoriteId } from '~/app/features/Gallery/hooks/useFavoriteId'

interface PropTypes {
  entityId: string
  maxColorSwatchesPerRow: number
  refValue: RefObject<HTMLDivElement> | null
}

const DesignTileWrapperComponent = ({ entityId, maxColorSwatchesPerRow, refValue }: PropTypes) => {
  const analytics = useAnalytics()
  const designById = useSelector(designByIdSelector)
  const quantity = useSelector(getQuantitySelector)
  const favoriteId = useFavoriteId(entityId)
  const viewId = useSelector(getViewId)
  const dpcId = useSelector(designPersonalizationContextIdSelector)

  const curryHandleOpenQuickView =
    (colorSwatchObjects: Gallery.ContentQuery.ColorSwatch[], currentDesignId: string, tileEntity: State.TileEntity) => (): void => {
      const color = designById(currentDesignId).color?.split('#')[1]
      const impressionIdFactory = (id: string): string => {
        const tileImpressionId = getEntityImpressionId(id)

        return generateImpressionId(viewId, tileImpressionId)
      }

      const isPersonalized = dpcId ? 'Quick view opened with personalized design' : ''

      analytics.trackEvent({
        action: ANALYTICS_EVENT_ACTIONS.GALLERY_DESIGN_ENGAGEMENT,
        eventLabel: ANALYTICS_LABEL.FLY_OUT_VIEWED,
        eventDetail: isPersonalized,
        ...analytics.buildDesignEngagement({
          engagementAction: ANALYTICS_LABEL.OPEN_QUICKVIEW,
          selectedDesign: currentDesignId,
          color,
          tileEntity,
          colorSwatchObjects,
          impressionId: impressionIdFactory,
          location: AnalyticsNames.DesignTile,
          position: tileEntity.position,
        }),
      })
    }

  return (
    <GalleryTileEntityProvider tileEntityId={entityId}>
      {(
        tileEntity,
        imageProps,
        handleColorSwatchChange,
        colorSwatchObjects,
        currentColorSwatch,
        currentDesignId,
        greetingOptions,
        greetingTitle,
        shouldShowGreetingOptions,
        shouldHideForSocialMedia,
        isPreviouslyOrdered,
        onMouseEnter,
      ) => (
        <DesignTile
          noFollow
          colorSwatches={colorSwatchObjects}
          currentDesignId={currentDesignId}
          entityId={entityId}
          favoriteId={favoriteId}
          greetingOptions={greetingOptions}
          greetingTitle={greetingTitle}
          handleColorSwatchChange={handleColorSwatchChange}
          imageProps={{
            ...imageProps,
            loading: tileEntity.position < DEFAULT_EAGER_IMAGES ? LOADING_TYPE.EAGER : LOADING_TYPE.LAZY,
          }}
          isPreviouslyOrdered={isPreviouslyOrdered}
          maxColorSwatchesPerRow={maxColorSwatchesPerRow}
          previewType={PREVIEW_TYPE.TILE}
          quantity={quantity}
          refValue={refValue}
          selectedDesignId={currentColorSwatch.designId}
          shouldHideForSocialMedia={shouldHideForSocialMedia}
          shouldShowGreetingOptions={shouldShowGreetingOptions}
          tileEntity={tileEntity}
          onLabelClick={curryHandleOpenQuickView(colorSwatchObjects, currentDesignId, tileEntity)}
          onMouseEnter={onMouseEnter}
        />
      )}
    </GalleryTileEntityProvider>
  )
}

export const DesignTileWrapper = memo(DesignTileWrapperComponent)
